import {preparedPromptPayloadChars, type PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {
  createHandoffStorage,
  type HandoffFailure,
  type HandoffReplaceResult,
} from '@github-ui/ahp-session-client/handoff-storage'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isAgentMode, type AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {CustomAgentSelectionRequest} from '@github-ui/ahp-session-client/custom-agent-selection'

import {isValidHttpUrl} from '../utils/is-valid-http-url'

export const ENVIRONMENT_TASK_LAUNCH_VERSION = 1
export const ENVIRONMENT_TASK_LAUNCH_UNCONSUMED_TTL_MS = 5 * 60 * 1000
export const ENVIRONMENT_TASK_LAUNCH_ACTIVE_TTL_MS = 30 * 60 * 1000
const ENVIRONMENT_TASK_LAUNCH_KEY_PREFIX = 'github.environment-task-launch.'

export type EnvironmentTaskLaunchPhase = 'task-created' | 'preparing' | 'dispatching' | 'failed'

export interface EnvironmentTaskLaunchRecord {
  version: typeof ENVIRONMENT_TASK_LAUNCH_VERSION
  taskId: string
  launchId: string
  revision: number
  owner: string
  createdAt: number
  expiresAt: number
  phase: EnvironmentTaskLaunchPhase
  requestId: string
  identity: {
    taskId: string
    sessionId: string
    environmentId: string
  }
  prompt: PreparedPrompt
  recovery: {
    managedSandbox: boolean
    baselineHostTools?: boolean
    repositoryNwo?: string
    repositoryCloneUrl?: string
    baseRef?: string
    /** Owner login a repository-less scoped session is bound to. */
    scopeLogin?: string
  }
  startupMode?: AgentMode
  customAgent?: CustomAgentSelectionRequest
  failure?: {
    message: string
    occurredAt: number
  }
}

export interface CreateEnvironmentTaskLaunchInput {
  taskId: string
  sessionId: string
  environmentId: string
  requestId: string
  prompt: PreparedPrompt
  managedSandbox: boolean
  baselineHostTools?: boolean
  repositoryNwo?: string
  repositoryCloneUrl?: string
  baseRef?: string
  scopeLogin?: string
  startupMode?: AgentMode
  customAgent?: CustomAgentSelectionRequest
}

export type EnvironmentTaskLaunchReadResult =
  | {status: 'available'; record: EnvironmentTaskLaunchRecord}
  | {status: 'expired'; record: EnvironmentTaskLaunchRecord}
  | {status: 'missing'}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function isPreparedPrompt(value: unknown): value is PreparedPrompt {
  if (!isRecord(value) || typeof value.text !== 'string') return false
  if (
    value.attachments !== undefined &&
    (!Array.isArray(value.attachments) ||
      !value.attachments.every(attachment => isRecord(attachment) && typeof attachment.type === 'string'))
  ) {
    return false
  }
  if (value.model !== undefined && !isRecord(value.model)) return false
  if (value.agent !== undefined && !isRecord(value.agent)) return false
  if (
    value.contributedInstructions !== undefined &&
    (!Array.isArray(value.contributedInstructions) ||
      !value.contributedInstructions.every(
        instruction =>
          isRecord(instruction) &&
          (instruction.kind === 'personal' ||
            instruction.kind === 'repository' ||
            instruction.kind === 'organization') &&
          (instruction.label === undefined || typeof instruction.label === 'string') &&
          (instruction.ownerUrl === undefined || typeof instruction.ownerUrl === 'string'),
      ))
  ) {
    return false
  }
  return true
}

function isCloneUrl(value: unknown): value is string {
  return typeof value === 'string' && isValidHttpUrl(value)
}

function isLaunchRecord(value: unknown): value is EnvironmentTaskLaunchRecord {
  if (
    !isRecord(value) ||
    value.version !== ENVIRONMENT_TASK_LAUNCH_VERSION ||
    !isNonEmptyString(value.taskId) ||
    !isNonEmptyString(value.launchId) ||
    !Number.isInteger(value.revision) ||
    (value.revision as number) < 0 ||
    !isNonEmptyString(value.owner) ||
    typeof value.createdAt !== 'number' ||
    typeof value.expiresAt !== 'number' ||
    (value.phase !== 'task-created' &&
      value.phase !== 'preparing' &&
      value.phase !== 'dispatching' &&
      value.phase !== 'failed') ||
    !isNonEmptyString(value.requestId) ||
    !isRecord(value.identity) ||
    value.identity.taskId !== value.taskId ||
    !isNonEmptyString(value.identity.sessionId) ||
    !isNonEmptyString(value.identity.environmentId) ||
    !isPreparedPrompt(value.prompt) ||
    !isRecord(value.recovery) ||
    typeof value.recovery.managedSandbox !== 'boolean'
  ) {
    return false
  }
  if (value.recovery.repositoryNwo !== undefined && !isNonEmptyString(value.recovery.repositoryNwo)) return false
  if (value.recovery.baselineHostTools !== undefined && typeof value.recovery.baselineHostTools !== 'boolean') {
    return false
  }
  if (value.recovery.repositoryCloneUrl !== undefined && !isCloneUrl(value.recovery.repositoryCloneUrl)) return false
  if (value.recovery.baseRef !== undefined && !isNonEmptyString(value.recovery.baseRef)) return false
  if (value.recovery.scopeLogin !== undefined && !isNonEmptyString(value.recovery.scopeLogin)) return false
  if (value.startupMode !== undefined && !isAgentMode(value.startupMode)) return false
  if (
    value.customAgent !== undefined &&
    (!isRecord(value.customAgent) ||
      !isNonEmptyString(value.customAgent.slug) ||
      (value.customAgent.displayName !== undefined && !isNonEmptyString(value.customAgent.displayName)))
  ) {
    return false
  }
  if (
    value.failure !== undefined &&
    (!isRecord(value.failure) ||
      typeof value.failure.message !== 'string' ||
      typeof value.failure.occurredAt !== 'number')
  ) {
    return false
  }
  return true
}

function storageKey(taskId: string): string {
  return `${ENVIRONMENT_TASK_LAUNCH_KEY_PREFIX}${taskId}`
}

function recordId(record: Pick<EnvironmentTaskLaunchRecord, 'launchId' | 'revision'>): string {
  return `${record.launchId}:${record.revision}`
}

function launchStorage(taskId: string) {
  return createHandoffStorage<EnvironmentTaskLaunchRecord>({
    key: storageKey(taskId),
    payloadChars: launch => preparedPromptPayloadChars(launch.prompt),
    validate: isLaunchRecord,
    getId: recordId,
    isStale: launch => launch.expiresAt <= Date.now(),
  })
}

export function createEnvironmentTaskLaunch(
  input: CreateEnvironmentTaskLaunchInput,
  now = Date.now(),
): EnvironmentTaskLaunchRecord {
  const owner = getCurrentUserLogin()
  if (!owner) throw new Error('An authenticated user is required to create an environment task launch.')

  return {
    version: ENVIRONMENT_TASK_LAUNCH_VERSION,
    taskId: input.taskId,
    launchId: input.requestId,
    revision: 0,
    owner,
    createdAt: now,
    expiresAt: now + ENVIRONMENT_TASK_LAUNCH_UNCONSUMED_TTL_MS,
    phase: 'task-created',
    requestId: input.requestId,
    identity: {
      taskId: input.taskId,
      sessionId: input.sessionId,
      environmentId: input.environmentId,
    },
    prompt: input.prompt,
    recovery: {
      managedSandbox: input.managedSandbox,
      ...(input.baselineHostTools !== undefined ? {baselineHostTools: input.baselineHostTools} : {}),
      repositoryNwo: input.repositoryNwo,
      repositoryCloneUrl: input.repositoryCloneUrl,
      baseRef: input.baseRef,
      scopeLogin: input.scopeLogin,
    },
    startupMode: input.startupMode,
    customAgent: input.customAgent,
  }
}

export function storeEnvironmentTaskLaunch(record: EnvironmentTaskLaunchRecord): HandoffFailure | null {
  return launchStorage(record.taskId).write(record)
}

export function inspectEnvironmentTaskLaunch(taskId: string): EnvironmentTaskLaunchReadResult {
  const record = launchStorage(taskId).read()
  if (
    !record ||
    record.taskId !== taskId ||
    record.identity.taskId !== taskId ||
    record.owner !== getCurrentUserLogin()
  ) {
    return {status: 'missing'}
  }
  return record.expiresAt <= Date.now() ? {status: 'expired', record} : {status: 'available', record}
}

export function readEnvironmentTaskLaunch(taskId: string): EnvironmentTaskLaunchRecord | null {
  const result = inspectEnvironmentTaskLaunch(taskId)
  return result.status === 'available' ? result.record : null
}

export function replaceEnvironmentTaskLaunch(
  taskId: string,
  update: (record: EnvironmentTaskLaunchRecord) => EnvironmentTaskLaunchRecord,
  expected?: Pick<EnvironmentTaskLaunchRecord, 'launchId' | 'revision'>,
): HandoffReplaceResult {
  const storage = launchStorage(taskId)
  const current = readEnvironmentTaskLaunch(taskId)
  if (!current) return 'superseded'
  if (expected && recordId(current) !== recordId(expected)) return 'superseded'
  return storage.replace(recordId(current), {...update(current), revision: current.revision + 1})
}

export function restartExpiredEnvironmentTaskLaunch(
  taskId: string,
  expected: Pick<EnvironmentTaskLaunchRecord, 'launchId' | 'revision'>,
  now = Date.now(),
): EnvironmentTaskLaunchRecord | null {
  const result = inspectEnvironmentTaskLaunch(taskId)
  if (result.status !== 'expired' || recordId(result.record) !== recordId(expected)) return null

  const restarted = {
    ...result.record,
    revision: result.record.revision + 1,
    phase: 'task-created' as const,
    expiresAt: now + ENVIRONMENT_TASK_LAUNCH_ACTIVE_TTL_MS,
    failure: undefined,
  }
  return launchStorage(taskId).replace(recordId(result.record), restarted) === null ? restarted : null
}

export function clearEnvironmentTaskLaunch(taskId: string): boolean {
  const record = readEnvironmentTaskLaunch(taskId)
  return record ? launchStorage(taskId).clear(recordId(record)) : false
}
